import { useMemo, useState } from 'react';
import { Map, Pencil, Plus, Trash2, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { BosDurum, Buton, OnayDialog, Rozet, VeriTablo, toast, type ColumnDef } from '@emanetx/ui';
import type { Ortak } from '@emanetx/sdk';
import { useNoktaListesi } from '../noktalar/api.js';
import { useOrtakNoktaAta, useOrtakNoktaCikar, useOrtakSil, useOrtaklar } from './api.js';
import { OrtakDialog } from './ortak-dialog.js';

export function SayfaOrtaklar() {
  const [sayfa, setSayfa] = useState(1);
  const [dialogAcik, setDialogAcik] = useState(false);
  const [duzenleHedef, setDuzenleHedef] = useState<Ortak | null>(null);
  const [noktaPanelOrtak, setNoktaPanelOrtak] = useState<Ortak | null>(null);
  const [silHedef, setSilHedef] = useState<Ortak | null>(null);

  const liste = useOrtaklar(sayfa, 50);
  const sil = useOrtakSil();

  const kolonlar = useMemo<ColumnDef<Ortak, unknown>[]>(
    () => [
      {
        accessorKey: 'ad',
        header: 'Şirket',
        meta: { etiket: 'Şirket' },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.ad}</div>
            <div className="text-fume-500 text-xs">{row.original.eposta}</div>
          </div>
        ),
      },
      {
        accessorKey: 'komisyonYuzde',
        header: 'Komisyon',
        meta: { etiket: 'Komisyon' },
        cell: ({ row }) => (
          <span className="font-mono text-sm font-semibold">
            %{Number(row.original.komisyonYuzde).toFixed(1)}
          </span>
        ),
      },
      {
        id: 'noktalar',
        header: 'Noktalar',
        meta: { etiket: 'Noktalar' },
        enableSorting: false,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Buton stil="cizgili" boyut="kucuk" onClick={() => setNoktaPanelOrtak(row.original)}>
              {row.original.noktalar.length} nokta
            </Buton>
          </div>
        ),
      },
      {
        accessorKey: 'telefon',
        header: 'Telefon',
        meta: { etiket: 'Telefon' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.telefon ?? '—'}</span>,
      },
      {
        accessorKey: 'aktif',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) =>
          row.original.aktif ? (
            <Rozet ton="yesil" nokta>
              Aktif
            </Rozet>
          ) : (
            <Rozet ton="gri" nokta>
              Pasif
            </Rozet>
          ),
      },
      {
        id: 'aksiyon',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Düzenle"
              onClick={() => {
                setDuzenleHedef(row.original);
                setDialogAcik(true);
              }}
            >
              <Pencil size={14} />
            </Buton>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Sil"
              onClick={() => setSilHedef(row.original)}
            >
              <Trash2 size={14} className="text-red-500" />
            </Buton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Ortaklar</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            İş ortakları, atanan noktalar ve komisyon oranları.
          </p>
        </div>
        <Buton
          onClick={() => {
            setDuzenleHedef(null);
            setDialogAcik(true);
          }}
        >
          <Plus size={16} /> Yeni Ortak
        </Buton>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={Map}
            baslik="Henüz ortak yok"
            aciklama="İlk iş ortağını ekleyerek başlayın."
          />
        }
      />

      <OrtakDialog
        acik={dialogAcik}
        onDegisim={(o) => {
          setDialogAcik(o);
          if (!o) setDuzenleHedef(null);
        }}
        ortak={duzenleHedef}
      />

      <OnayDialog
        acik={!!silHedef}
        onDegisim={(o) => !o && setSilHedef(null)}
        baslik="Ortağı sil?"
        aciklama={silHedef?.ad}
        onayMetni="Sil"
        tur="tehlike"
        onOnay={async () => {
          if (!silHedef) return;
          try {
            await sil.mutateAsync(silHedef.id);
            toast.success('Ortak silindi');
            setSilHedef(null);
          } catch (err) {
            const m =
              err && typeof err === 'object' && 'message' in err
                ? String((err as { message: string }).message)
                : 'Silinemedi';
            toast.error(m);
          }
        }}
      />

      <NoktaPanel ortak={noktaPanelOrtak} onKapat={() => setNoktaPanelOrtak(null)} />
    </div>
  );
}

function NoktaPanel({ ortak, onKapat }: { ortak: Ortak | null; onKapat: () => void }) {
  const noktalar = useNoktaListesi({ sayfa: 1, boyut: 100 });
  const ata = useOrtakNoktaAta();
  const cikar = useOrtakNoktaCikar();
  const [komisyon, setKomisyon] = useState('');

  if (!ortak) return null;

  const atananIds = new Set(ortak.noktalar.map((n) => n.noktaId));
  const atanabilir = noktalar.data?.veriler.filter((n) => !atananIds.has(n.id)) ?? [];

  return (
    <Dialog.Root open={!!ortak} onOpenChange={(o) => !o && onKapat()}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">{ortak.ad} — Noktalar</Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Kapat" className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-4">
            <section>
              <h3 className="mb-2 text-sm font-semibold">Atanmış Noktalar</h3>
              {ortak.noktalar.length === 0 ? (
                <p className="text-fume-500 text-sm">Henüz nokta atanmamış.</p>
              ) : (
                <ul className="divide-fume-100 dark:divide-fume-800 divide-y">
                  {ortak.noktalar.map((n) => (
                    <li key={n.noktaId} className="flex items-center justify-between py-2">
                      <div>
                        <span className="text-marka-700 dark:text-marka-400 font-mono text-xs">
                          {n.noktaKod}
                        </span>
                        <span className="ml-2 text-sm">{n.noktaAd}</span>
                        {n.komisyonYuzde && (
                          <span className="text-fume-400 ml-2 text-xs">
                            komisyon %{n.komisyonYuzde}
                          </span>
                        )}
                      </div>
                      <Buton
                        stil="sade"
                        boyut="simge"
                        aria-label="Çıkar"
                        onClick={async () => {
                          try {
                            await cikar.mutateAsync({ ortakId: ortak.id, noktaId: n.noktaId });
                            toast.success('Nokta çıkarıldı');
                          } catch {
                            toast.error('Çıkarılamadı');
                          }
                        }}
                      >
                        <X size={14} className="text-red-500" />
                      </Buton>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="border-fume-100 dark:border-fume-800 border-t pt-4">
              <h3 className="mb-2 text-sm font-semibold">Nokta Ata</h3>
              {atanabilir.length === 0 ? (
                <p className="text-fume-500 text-sm">Atanacak nokta kalmadı.</p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <label className="text-fume-500 text-xs">Bu atamada komisyon %</label>
                    <input
                      value={komisyon}
                      onChange={(e) => setKomisyon(e.target.value)}
                      placeholder="opsiyonel (ortak default)"
                      className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 h-8 w-40 rounded-md border bg-white px-2 text-xs"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {atanabilir.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={async () => {
                          try {
                            await ata.mutateAsync({
                              ortakId: ortak.id,
                              noktaIdler: [n.id],
                              ...(komisyon &&
                                /^\d+(\.\d{1,2})?$/.test(komisyon) && { komisyonYuzde: komisyon }),
                            });
                            toast.success('Atandı');
                          } catch {
                            toast.error('Atanamadı');
                          }
                        }}
                        className="border-fume-200 hover:bg-fume-50 dark:border-fume-700 dark:bg-fume-900 dark:hover:bg-fume-800 rounded-md border bg-white px-3 py-1.5 text-xs"
                      >
                        <span className="text-marka-700 dark:text-marka-400 font-mono">
                          {n.kod}
                        </span>{' '}
                        — {n.ad}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
